import type { WorkspaceFileInfo } from "@remote-agents/shared";

/** Keep operational fixtures available without making them the default reading list. */
export function isSupportingDocument(path: string): boolean {
  const normalized = path.replace(/\\/g, "/");
  return /(^|\/)(?:__tests__|tests?|fixtures?|test-results|coverage|screenshots)(\/|$)/i.test(normalized)
    || /(^|\/)(?:AGENTS|CLAUDE|SKILL)\.md$/i.test(normalized)
    || /(^|\/)(?:smoke|e2e|test-fixture|end-to-end-validation-task)(?:[-_.]|$)/i.test(normalized);
}

function generatedDocument(path: string): { kind: string; title: string | undefined; date: string; hour: string; minute: string } | null {
  const filename = (path.split("/").pop() ?? path).replace(/\.(?:md|markdown)$/i, "");
  // Recognize generated names precisely; authored guides such as
  // session-management.md should keep their normal title and priority.
  const match = /^(session|report)-(?:(.+)-)?(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-\d{2}-\d{3}Z-[a-z\d-]+$/i.exec(filename);
  return match ? { kind: match[1], title: match[2], date: match[3], hour: match[4], minute: match[5] } : null;
}

export function documentTitle(path: string): string {
  const session = generatedDocument(path);
  if (session) {
    if (!session.title) return `${session.kind === "session" ? "Task summary" : "Report"} · ${session.date} ${session.hour}:${session.minute} UTC`;
    const title = session.title.replace(/[-_]+/g, " ");
    return title.charAt(0).toUpperCase() + title.slice(1);
  }
  const title = (path.split("/").pop() ?? path).replace(/\.(?:md|markdown)$/i, "").replace(/[-_]+/g, " ");
  // Single-word conventional names (README, AGENTS) stay recognizable, while
  // descriptive SCREAMING_SNAKE_CASE filenames become comfortable card titles.
  return /[A-Z]/.test(title) && title === title.toUpperCase() && /\s/.test(title)
    ? title.charAt(0) + title.slice(1).toLowerCase()
    : title;
}

function documentPriority(path: string): number {
  if (generatedDocument(path)?.kind === "session") return 3;
  if (/^(?:reports\/|\.remote-agents\/artifacts\/)/i.test(path)) return 0;
  return path.includes("/") ? 2 : 1;
}

export function selectDocuments(files: WorkspaceFileInfo[], query: string, includeSupporting: boolean, sort: "recommended" | "recent" | "name"): WorkspaceFileInfo[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return files.filter((file) => (includeSupporting || words.length > 0 || !isSupportingDocument(file.path))
    && words.every((word) => file.path.toLowerCase().includes(word)))
    .sort((a, b) => {
      if (sort === "name") return a.path.localeCompare(b.path);
      return (sort === "recommended" ? documentPriority(a.path) - documentPriority(b.path) : 0) || b.mtime - a.mtime || a.path.localeCompare(b.path);
    });
}

/** Resolve only workspace-relative Markdown links; the server still enforces file boundaries. */
export function linkedDocument(currentPath: string, href: string): string | null {
  if (/^(?:[a-z][a-z\d+.-]*:|\/|#)/i.test(href)) return null;
  let path: string;
  try { path = decodeURIComponent(href.split(/[?#]/)[0]); } catch { return null; }
  if (!/\.(?:md|markdown)$/i.test(path) || path.includes("\\")) return null;
  const parts = currentPath.split("/").slice(0, -1);
  for (const part of path.split("/")) {
    if (part === "..") { if (!parts.length) return null; parts.pop(); }
    else if (part && part !== ".") parts.push(part);
  }
  return parts.join("/");
}

/** GitHub-style heading fragments, including repeated heading disambiguation. */
export function headingAnchors(titles: string[]): string[] {
  const counts = new Map<string, number>();
  return titles.map((title) => {
    const base = title.trim().toLowerCase().replace(/[^\p{L}\p{N}\s_-]/gu, "").replace(/\s/g, "-");
    const occurrence = counts.get(base) ?? 0;
    counts.set(base, occurrence + 1);
    return occurrence ? `${base}-${occurrence}` : base;
  });
}
